import { useEffect, useRef, useState } from 'react';
import { authFetch, type AuthSession } from '../auth';
import type { Document } from './dueDiligence';
import {
  transferRecording,
  type RecordingUpload,
  type TransferProgress,
} from './recordingTransfer';
export function fileSize(bytes: number) {
  return bytes < 1024 * 1024
    ? `${Math.ceil(bytes / 1024).toLocaleString('de-DE')} KB`
    : bytes >= 1024 * 1024 * 1024
      ? `${(bytes / 1024 / 1024 / 1024).toLocaleString('de-DE', { maximumFractionDigits: 1 })} GB`
      : `${(bytes / 1024 / 1024).toLocaleString('de-DE', { maximumFractionDigits: 1 })} MB`;
}
export function RecordingUploader({
  user,
  projectId,
  onStored,
}: {
  user: AuthSession;
  projectId: string;
  onStored: (document: Document) => Promise<void>;
}) {
  const base = `/api/research/properties/${encodeURIComponent(projectId)}/due-diligence/recording-uploads`;
  const [pending, setPending] = useState<RecordingUpload[]>([]);
  const [progress, setProgress] = useState<TransferProgress | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const controller = useRef<AbortController | null>(null);
  const alive = useRef(true);
  const stored = useRef(onStored);
  useEffect(() => {
    stored.current = onStored;
  }, [onStored]);
  useEffect(() => {
    alive.current = true;
    const abort = new AbortController();
    void authFetch(user, base, { signal: abort.signal })
      .then((data) => alive.current && setPending(data.items))
      .catch(() => {});
    return () => {
      alive.current = false;
      abort.abort();
      controller.current?.abort();
    };
  }, [user, base]);
  const reload = async () => {
    const data = await authFetch(user, base);
    if (alive.current) setPending(data.items);
  };
  const transfer = async (file: File, resume?: RecordingUpload) => {
    const abort = new AbortController();
    controller.current = abort;
    setBusy(true);
    setError('');
    setMessage('');
    setProgress(null);
    try {
      const document = await transferRecording(file, {
        base,
        api: (path, init) => authFetch(user, path, init),
        signal: abort.signal,
        resume,
        onProgress: (value) => alive.current && setProgress(value),
      });
      if (alive.current) {
        await stored.current(document);
        setProgress(null);
        setMessage(
          'Originalaufnahme gespeichert. Die Analyse startet nur mit deiner Freigabe.',
        );
      }
    } catch (reason) {
      if (alive.current) {
        if (abort.signal.aborted)
          setMessage(
            'Upload pausiert. Bestätigte Teile bleiben 24 Stunden erhalten; dieselbe Datei auswählen, um fortzusetzen.',
          );
        else
          setError(
            reason instanceof Error
              ? reason.message
              : 'Upload unterbrochen. Bestätigte Teile bleiben erhalten.',
          );
      }
    } finally {
      if (controller.current === abort) controller.current = null;
      if (alive.current) {
        await reload().catch(() => {});
        setBusy(false);
      }
    }
  };
  const discard = async (upload: RecordingUpload) => {
    setBusy(true);
    setError('');
    try {
      await authFetch(user, `${base}/${encodeURIComponent(upload.id)}`, {
        method: 'DELETE',
      });
      await reload();
      setMessage(
        'Unvollständiger Upload verworfen. Gespeicherte Originale bleiben erhalten.',
      );
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : 'Upload konnte nicht verworfen werden.',
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="journey-recording-import">
      <label className="journey-upload">
        <strong>Besuchsaufnahme hinzufügen</strong>
        <span>Audio oder Video bis 2 GB · unterbrechbar und fortsetzbar</span>
        <input
          aria-label="Besuchsaufnahme hochladen"
          type="file"
          accept="audio/*,video/mp4,video/quicktime,video/webm"
          disabled={busy}
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (file) void transfer(file);
          }}
        />
      </label>
      <p className="journey-caption">
        Das Original bleibt als Quelle erhalten. Unvollständige Uploads kannst
        du bis 24 Stunden nach dem letzten übertragenen Teil fortsetzen; danach
        werden diese Teile entfernt. Für die Analyse müssen die Einwilligungen
        der Beteiligten geklärt sein.
      </p>
      {busy && (
        <div className="journey-transfer" role="status" aria-live="polite">
          <strong>{progress?.upload.name ?? 'Aufnahme vorbereiten…'}</strong>
          {progress && (
            <>
              <progress
                max={progress.upload.totalSize}
                value={progress.upload.offset}
                aria-label="Fortschritt der Besuchsaufnahme"
              />
              <span>
                {fileSize(progress.upload.offset)} von{' '}
                {fileSize(progress.upload.totalSize)} ·{' '}
                {progress.phase === 'checking'
                  ? 'Vorhandene Teile abgleichen'
                  : progress.phase === 'finalizing'
                    ? 'Vollständiges Original prüfen'
                    : `${Math.floor((progress.upload.offset / progress.upload.totalSize) * 100)} % gespeichert`}
              </span>
            </>
          )}
          <button
            className="quiet-button"
            onClick={() => controller.current?.abort()}
          >
            Upload pausieren
          </button>
        </div>
      )}
      {message && (
        <p role="status" className="journey-message">
          {message}
        </p>
      )}
      {error && (
        <p role="alert" className="journey-alert">
          {error}
        </p>
      )}
      {!busy &&
        pending.map((upload) => (
          <article className="journey-transfer" key={upload.id}>
            <strong>{upload.name}</strong>
            <span>
              {fileSize(upload.offset)} von {fileSize(upload.totalSize)}{' '}
              gespeichert · fortsetzbar bis{' '}
              {new Date(upload.expiresAt).toLocaleString('de-DE')}
            </span>
            <label className="journey-resume">
              Originaldatei zum Fortsetzen auswählen
              <input
                aria-label={`Upload fortsetzen: ${upload.name}`}
                type="file"
                accept="audio/*,video/mp4,video/quicktime,video/webm"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  event.target.value = '';
                  if (file) void transfer(file, upload);
                }}
              />
            </label>
            <button
              className="quiet-button"
              onClick={() => void discard(upload)}
            >
              Unvollständigen Upload verwerfen
            </button>
          </article>
        ))}
    </div>
  );
}
